import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import Card from '@/shared/components/Card';
import {AdminService} from '@/modules/admin/services/adminService';
import {UserEditModal} from '@/modules/admin/components/UserEditModal';
import {Loader2} from 'lucide-react';
import {logError} from '@/shared/utils/logger';
import type {UserSearchRequest, UserSearchResult} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';

interface Props {
    setPinnedMetrics?: (value: any) => void;
    initialUserSearch?: {
        userId?: number | null;
        firstName?: string | null;
        lastName?: string | null;
        birthday?: string | null;
    } | null;
}

type SearchFields = {
    orderId: string;
    firstName: string;
    lastName: string;
    email: string;
    birthday: string;
    phone: string;
};

type OrderSearchFields = {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
};

type OrderResult = {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
    birthdate: string;
    status: string;
    rejectedReason?: string | null;
    rejectedCode?: string | null;
};

type Toast = {
    error: boolean;
    message: string;
};

export const AdminSearch: React.FC<Props> = ({setPinnedMetrics = () => {}, initialUserSearch = null}) => {
    void setPinnedMetrics;
    const [fields, setFields] = useState<SearchFields>({
        orderId: '',
        firstName: '',
        lastName: '',
        email: '',
        birthday: '',
        phone: '',
    });
    const [debouncedFields, setDebouncedFields] = useState<SearchFields>({
        orderId: '',
        firstName: '',
        lastName: '',
        email: '',
        birthday: '',
        phone: '',
    });
    const [toast, setToast] = useState<Toast>({error: false, message: ''});
    const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
    const [orderResults, setOrderResults] = useState<OrderResult[]>([]);
    const [isModalHidden, setIsModalHidden] = useState(true);
    const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
    const autoSearchKeyRef = React.useRef<string | null>(null);
    const autoOpenPendingRef = React.useRef(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const userSearchMutation = useMutation({
        mutationFn: (searchCriteria: UserSearchRequest) => AdminService.searchUsers(searchCriteria),
        onSuccess: (data) => {
            setUserResults(data);
            if (data.length === 0) {
                setToast({error: false, message: 'No User Found.'});
            } else {
                setToast({error: false, message: ''});
            }
        },
        onError: (error) => {
            logError('Search error', error);
            setToast({error: true, message: 'Error searching users. Please try again.'});
        },
    });

    const orderSearchMutation = useMutation({
        mutationFn: (searchPayload: OrderSearchFields) => AdminService.searchOrders(searchPayload),
        onSuccess: (data) => {
            const processedData = data.map((order: any) => ({
                ...order,
                birthdate: order.birthdate ? formatBirthdate(order.birthdate) : null,
                rejectedReason: order.rejected_reason ?? order.rejectedReason ?? null,
                rejectedCode: order.rejected_code ?? order.rejectedCode ?? null,
            }));
            setOrderResults(processedData);
            if (processedData.length === 0) {
                setToast({error: false, message: 'No Order Found.'});
            } else {
                setToast({error: false, message: ''});
            }
        },
        onError: (error) => {
            logError('Order search error', error);
            setToast({error: true, message: 'Error searching orders. Please try again.'});
            setOrderResults([]);
        },
    });

    const userSearchMutateRef = useRef(userSearchMutation.mutate);
    const orderSearchMutateRef = useRef(orderSearchMutation.mutate);

    const handleInputChange = useCallback(
        (field: keyof SearchFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setFields((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        },
        []
    );

    const handleBirthdayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const digitReg = /^[0-9]$/;
        let value = event.target.value;

        if (value.length === 3 || value.length === 6) {
            if (value[value.length - 1] !== '/') {
                value = value.substring(0, value.length - 1) + '/' + value[value.length - 1];
            } else {
                value = value.substring(0, value.length - 1);
            }
        }

        if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 10)) {
            return;
        }

        setFields((prev) => ({...prev, birthday: value}));
    }, []);

    const handlePhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 10);
        let formatted = '';
        if (digits.length > 0) {
            if (digits.length <= 3) {
                formatted = `(${digits}`;
            } else if (digits.length <= 6) {
                formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
            } else {
                formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
            }
        }
        setFields((prev) => ({...prev, phone: formatted}));
    }, []);

    const validateFields = useCallback((searchFields: SearchFields): string | null => {
        if (searchFields.birthday && isNaN(Number(new Date(searchFields.birthday)))) {
            return 'Invalid date of birth. Please use this format: MM/DD/YYYY';
        }
        return null;
    }, []);

    const runSearch = useCallback(
        (searchFields: SearchFields) => {
            setToast({error: false, message: ''});

            const validationError = validateFields(searchFields);
            if (validationError) {
                setToast({error: true, message: validationError});
                return;
            }

            const hasUserCriteria =
                !!searchFields.firstName ||
                !!searchFields.lastName ||
                !!searchFields.email ||
                !!searchFields.birthday ||
                !!searchFields.phone;
            const hasOrderCriteria =
                !!searchFields.orderId || !!searchFields.firstName || !!searchFields.lastName || !!searchFields.email;

            if (!hasUserCriteria && !hasOrderCriteria) {
                setUserResults([]);
                setOrderResults([]);
                setToast({error: false, message: ''});
                return;
            }

            if (hasUserCriteria) {
                userSearchMutateRef.current({
                    firstName: searchFields.firstName,
                    lastName: searchFields.lastName,
                    email: searchFields.email,
                    birthday: searchFields.birthday,
                    phone: searchFields.phone,
                });
            } else {
                setUserResults([]);
            }

            if (hasOrderCriteria) {
                orderSearchMutateRef.current({
                    oid: searchFields.orderId,
                    firstName: searchFields.firstName,
                    lastName: searchFields.lastName,
                    email: searchFields.email,
                });
            } else {
                setOrderResults([]);
            }
        },
        [validateFields]
    );

    const handleEditUser = useCallback((user: UserSearchResult) => {
        setSelectedUser(user);
        setIsModalHidden(false);
    }, []);

    const handleUserUpdated = useCallback(() => {
        if (userSearchMutation.data) {
            if (fields.firstName || fields.lastName || fields.email || fields.birthday || fields.phone) {
                userSearchMutation.mutate({
                    firstName: fields.firstName,
                    lastName: fields.lastName,
                    email: fields.email,
                    birthday: fields.birthday,
                    phone: fields.phone,
                });
            }
        }
    }, [fields, userSearchMutation]);

    const formatBirthday = useCallback((dateString: string) => {
        return formatBirthdate(dateString);
    }, []);

    const formatOrderDate = useCallback((dateString: string) => {
        if (!dateString) return '';
        return dateString.split('T')[0];
    }, []);

    React.useEffect(() => {
        if (!initialUserSearch) return;
        const formattedBirthday = formatBirthdate(initialUserSearch.birthday) ?? '';
        const key = JSON.stringify({
            userId: initialUserSearch.userId ?? null,
            firstName: initialUserSearch.firstName ?? '',
            lastName: initialUserSearch.lastName ?? '',
            birthday: formattedBirthday,
        });
        if (autoSearchKeyRef.current === key) return;
        autoSearchKeyRef.current = key;
        autoOpenPendingRef.current = true;
        setFields((prev) => ({
            ...prev,
            firstName: initialUserSearch.firstName ?? '',
            lastName: initialUserSearch.lastName ?? '',
            birthday: formattedBirthday,
        }));
        userSearchMutation.mutate({
            firstName: initialUserSearch.firstName ?? '',
            lastName: initialUserSearch.lastName ?? '',
            birthday: formattedBirthday,
            email: '',
            phone: '',
        });
    }, [initialUserSearch, userSearchMutation]);

    React.useEffect(() => {
        if (!autoOpenPendingRef.current) return;
        if (!userSearchMutation.data || userSearchMutation.data.length === 0) return;
        const targetId = initialUserSearch?.userId;
        const match =
            typeof targetId === 'number'
                ? userSearchMutation.data.find((user) => user.id === targetId)
                : userSearchMutation.data[0];
        if (!match) return;
        handleEditUser(match);
        autoOpenPendingRef.current = false;
    }, [handleEditUser, initialUserSearch?.userId, userSearchMutation.data]);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setDebouncedFields(fields);
        }, 350);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [fields]);

    useEffect(() => {
        runSearch(debouncedFields);
    }, [debouncedFields, runSearch]);

    useEffect(() => {
        userSearchMutateRef.current = userSearchMutation.mutate;
    }, [userSearchMutation.mutate]);

    useEffect(() => {
        orderSearchMutateRef.current = orderSearchMutation.mutate;
    }, [orderSearchMutation.mutate]);

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-mld-text-primary">Search</h2>
                            <p className="text-sm text-mld-text-secondary">
                                Find users or orders within the current organization.
                            </p>
                        </div>
                    </div>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={(event) => event.preventDefault()} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            <div>
                                <label
                                    htmlFor="searchOrders-oid"
                                    className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    Order ID
                                </label>
                                <input
                                    id="searchOrders-oid"
                                    type="text"
                                    value={fields.orderId}
                                    onChange={handleInputChange('orderId')}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label htmlFor="search-fname" className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    First Name
                                </label>
                                <input
                                    id="search-fname"
                                    type="text"
                                    value={fields.firstName}
                                    onChange={handleInputChange('firstName')}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label htmlFor="search-lname" className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    Last Name
                                </label>
                                <input
                                    id="search-lname"
                                    type="text"
                                    value={fields.lastName}
                                    onChange={handleInputChange('lastName')}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label htmlFor="search-email" className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    Email
                                </label>
                                <input
                                    id="search-email"
                                    type="email"
                                    value={fields.email}
                                    onChange={handleInputChange('email')}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="search-birthday"
                                    className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    Date of Birth
                                </label>
                                <input
                                    id="search-birthday"
                                    type="text"
                                    value={fields.birthday}
                                    placeholder="MM/DD/YYYY"
                                    onChange={handleBirthdayChange}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label htmlFor="search-phone" className="block text-sm font-medium text-mld-text-secondary mb-1">
                                    Phone Number
                                </label>
                                <input
                                    id="search-phone"
                                    type="tel"
                                    value={fields.phone}
                                    onChange={handlePhoneChange}
                                    inputMode="tel"
                                    maxLength={14}
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div className="flex items-end">
                                {(userSearchMutation.isPending || orderSearchMutation.isPending) && (
                                    <div className="flex items-center gap-2 text-sm text-mld-muted-text">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Searching…
                                    </div>
                                )}
                            </div>
                        </div>

                        {toast.message && (
                            <div
                                className="inline-flex rounded-md px-3 py-2 text-sm text-white"
                                style={{backgroundColor: toast.error ? '#F15E87' : '#42D4B0'}}>
                                {toast.message}
                            </div>
                        )}
                    </form>
                </Card.Content>
            </Card>

            {userResults.length > 0 ? (
                <Card>
                    <Card.Header>
                        <Card.Title>Search Results ({userResults.length} users found)</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-mld-panel-border">
                                <thead className="bg-mld-menu-background">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Action
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            First Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Last Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Birthday
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-mld-panel-border">
                                    {userResults.map((user, index) => (
                                        <tr key={user.id || index} className="hover:bg-mld-menu-background">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => handleEditUser(user)}
                                                    className="text-mld-primary hover:text-primary-900 font-medium">
                                                    Edit
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {user.firstName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {user.lastName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {formatBirthday(user.birthday)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card.Content>
                </Card>
            ) : null}

            {orderResults.length > 0 ? (
                <Card>
                    <Card.Header>
                        <Card.Title>Search Results ({orderResults.length} orders found)</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-mld-panel-border">
                                <thead className="bg-mld-menu-background">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Order ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            First Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Last Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Birthday
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-mld-panel-border">
                                    {orderResults.map((order: OrderResult, index) => (
                                        <tr key={order.oid || index} className="hover:bg-mld-menu-background">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-mld-text-primary">
                                                {order.oid}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {order.firstName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {order.lastName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {order.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                {formatOrderDate(order.birthdate)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-mld-text-secondary">{order.status}</span>
                                                {order.rejectedReason && (
                                                    <div
                                                        className="mt-0.5 max-w-xs text-xs text-mld-danger"
                                                        title={order.rejectedCode ? `Lab code: ${order.rejectedCode}` : undefined}>
                                                        Rejection reason: {order.rejectedReason}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card.Content>
                </Card>
            ) : null}

            {selectedUser && !isModalHidden ? (
                <UserEditModal
                    isModalHidden={isModalHidden}
                    setIsModalHidden={setIsModalHidden}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    onUserUpdated={handleUserUpdated}
                />
            ) : null}
        </div>
    );
};

export default AdminSearch;
